import React, { useContext } from "react"
import { useLocation, useParams } from "react-router-dom"

import { Icons } from "@izettle/otto"
import { useLocalization } from "@izettle/tenko-react"

import { REACT_APP_SUPPORT_URL } from "@/config/environment"
import { zettleBrandLogoPrimaryPositive } from "@/core/assets/graphics"
import { ConfigContext } from "@/core/context/config"
import {
  availableLinkLocales,
  defaultLocale,
  OPT_OUT_FAILURE_STATUS,
  OPT_OUT_SUCCESS_STATUS,
} from "@/core/utils/constant"
import { getSupportedLocale } from "@/core/utils/get-supported-locale"
import { OptOutFooter } from "@/modules/opt-out/components/faq-footer"
import { useOptOutStatusTracking } from "@/modules/opt-out/hooks/tracking"
import {
  OptOutBox,
  OptOutBoxContent,
  Divider,
  Logo,
  PageContainer,
  Icon,
  StatusTitle,
  SupportLink,
  OptOutText,
} from "@/modules/opt-out/styles"

const OptOutStatusBox = (props: any) => {
  return (
    <PageContainer>
      <Logo src={zettleBrandLogoPrimaryPositive} alt="Logo" />
      <OptOutBox className="App">
        <OptOutBoxContent>
          {props.children}
          <Divider />
          <OptOutFooter />
        </OptOutBoxContent>
      </OptOutBox>
    </PageContainer>
  )
}

const FailureComponent = (props: any) => {
  const { id } = useParams<{ id: string }>()
  useOptOutStatusTracking(id, OPT_OUT_FAILURE_STATUS)

  return (
    <OptOutStatusBox>
      <StatusTitle status={OPT_OUT_FAILURE_STATUS}>
        <Icon icon={Icons.CircledCross} />
        {props.title}
      </StatusTitle>
      {props.children}
    </OptOutStatusBox>
  )
}

const DigitalReceiptsOptSuccessStatus = () => {
  const { id } = useParams<{ id: string }>()
  useOptOutStatusTracking(id, OPT_OUT_SUCCESS_STATUS)
  const { text } = useLocalization()

  return (
    <OptOutStatusBox>
      <StatusTitle status={OPT_OUT_SUCCESS_STATUS}>
        <Icon icon={Icons.CircledTick} />
        {text("OPT_OUT_SUCCESS_TITLE")}
      </StatusTitle>
      <OptOutText>{text("OPT_OUT_SUCCESS_CONTENT")}</OptOutText>
      <OptOutText>{text("OPT_OUT_CONTENT_PAYPAL_SERVICE_NOTE")}</OptOutText>
    </OptOutStatusBox>
  )
}

const DigitalReceiptsOptFailureStatus = () => {
  const { text } = useLocalization()
  return (
    <FailureComponent title={text("OPT_OUT_FAILURE_TITLE")}>
      {text("OPT_OUT_FAILURE_CONTENT")}
    </FailureComponent>
  )
}

const DigitalReceiptsOptOutBadRequestStatus = () => {
  const { text } = useLocalization()
  const config = useContext(ConfigContext)
  const linkLocale = getSupportedLocale(
    config.locale,
    availableLinkLocales,
    defaultLocale,
  ).territory.toLowerCase()

  return (
    <FailureComponent title={text("OPT_OUT_CLIENT_FAILURE_TITLE")}>
      {text(
        "OPT_OUT_CLIENT_FAILURE_CONTENT",
        // eslint-disable-next-line react/jsx-key
        [
          <SupportLink
            key="support_link"
            target="_blank"
            href={REACT_APP_SUPPORT_URL.replace("{0}", linkLocale)}
          >
            {text("SUPPORT_LINK")}
          </SupportLink>,
        ],
      )}
    </FailureComponent>
  )
}

export const DigitalReceiptsOptOutStatus = () => {
  const query = new URLSearchParams(useLocation().search)
  const status = query.get("status") ?? ""
  const reason = query.get("reason") ?? ""

  if (status === OPT_OUT_SUCCESS_STATUS) {
    return <DigitalReceiptsOptSuccessStatus />
  } else if (/^4[\d]{2}/.test(reason)) {
    return <DigitalReceiptsOptOutBadRequestStatus />
  } else {
    return <DigitalReceiptsOptFailureStatus />
  }
}
